import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import classes from './FilterCheckbox.module.css';
import { ReactComponent as Check } from '../../../../../icons/CheckIcon.svg';
import { ReactComponent as FlipToggle } from '../../../../../icons/FlipToggle.svg';
import { toPascalCase } from '../../../../../utils/toPascalCase';

interface Props {
  options: { id: string | number; text: string }[] | undefined;
  title: string;
}

export const FilterCheckbox: FC<Props> = ({ options, title }) => {
  const MAX_DISPLAYED_OPTIONS = 4;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOptions, setSelectedOptions] = useState<(string | number)[]>(
    []
  );
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);

  useEffect(() => {
    const optionsParams = searchParams.get(toPascalCase(title))?.split(',');
    if (optionsParams) {
      setSelectedOptions([...optionsParams]);
    }
  }, [searchParams, title]);

  const handleCheckboxChange = (optionId: string | number) => {
    let newSearch;
    if (selectedOptions.includes(optionId)) {
      const newParams = new URLSearchParams(searchParams);
      setSelectedOptions((prevState) =>
        prevState.filter((item) => item !== optionId)
      );

      newSearch = newParams
        .get(toPascalCase(title))
        ?.split(',')
        .filter((item) => item !== optionId)
        ?.join(',');

      if (newSearch && newSearch !== '') {
        newParams.set(toPascalCase(title), newSearch);
        setSearchParams(newParams);
      } else {
        newParams.delete(toPascalCase(title));
        setSearchParams(newParams);
      }
    } else {
      const newParams = new URLSearchParams(searchParams);
      setSelectedOptions((prevState) => prevState.concat(optionId));
      newSearch = newParams.get(toPascalCase(title))
        ? `${newParams.get(toPascalCase(title))},${optionId}`
        : optionId.toString();
      newParams.set(toPascalCase(title), newSearch);
      setSearchParams(newParams);
    }
  };

  const visibleOptions = showAdditionalOptions
    ? options
    : options?.slice(0, MAX_DISPLAYED_OPTIONS);

  return (
    <div className={classes.FilterContainer}>
      <p className={classes.Title}>{title}</p>
      {visibleOptions &&
        visibleOptions.map((option, index) => (
          <label
            key={option.id}
            htmlFor={`checkbox-${option.id}`}
            className={classes.Checkbox}
          >
            <input
              type="checkbox"
              data-testid={`checkbox${index}`}
              value={option.id}
              checked={selectedOptions.includes(option.id)}
              onChange={() => {
                handleCheckboxChange(option.id);
              }}
            />
            <span className={classes.Checkmark}>
              {selectedOptions.includes(option.id) && <Check fill="#fff" />}
            </span>
            {option.text}
          </label>
        ))}

      {options && options.length > MAX_DISPLAYED_OPTIONS && (
        <button
          type="button"
          onClick={() => setShowAdditionalOptions(!showAdditionalOptions)}
          className={`${classes.ShowMore} text-internal-main text-xs`}
        >
          {!showAdditionalOptions ? 'Show more' : 'Show less'}
          <FlipToggle transform={`scale(${showAdditionalOptions ? -1 : 1})`} />
        </button>
      )}
    </div>
  );
};
